/**
 * AccountForm - New Account / Edit Account (the Accounts tab's modal).
 *
 * A new account starts the lead journey, so it needs a stage and a lead
 * source (Paul Q8) and a first task - owner, what happens next, due date
 * (T-03 / Q11) - saved in the same request as the account (first_task).
 * Designation starts as Lead; Customer can be picked for a company we already
 * sell to, and Account (Paul, 10/21/26) for one we track that is not a lead.
 * An Account is not on the lead journey, so its lead source and first task are
 * optional. `defaultDesignation` starts the form elsewhere: the Opportunities
 * tab's "Create from screenshot" makes the account an opportunity needs, so it
 * starts as an Account. Bulk import posts Account too (ImportModal).
 *
 * Editing keeps to the company's own fields; stage, designation and status are
 * set on the Lead tab of the account itself.
 *
 * "Create from screenshot / text" (components/aiCreate) opens a NEW account
 * with `prefill` (the company fields it read - stage, lead source and the
 * first task are still the person's to fill) and hears the saved row through
 * `onSaved`. Neither changes the form for anyone else.
 */
import { useState, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { Toast } from '../ui/Toast';
import {
  TIERS,
  TIER_HINT,
  VERTICALS,
  LEAD_SOURCES,
  CRM_STAGES,
  DEFAULT_STAGE,
  STAGE_DEFINITIONS,
  DESIGNATIONS,
  MEETING_GATE_STAGE,
  isUntrackedLead,
  DESIGNATION_DEFINITIONS,
} from '../../data/crmStages';
import { firstTaskErrors, taskPayload } from '../crm/crmFormHelpers';
import TagPeople from '../tasks/TagPeople';

const LABEL = { fontSize: 10.5, fontWeight: 700, color: '#666', marginBottom: 4, display: 'block' };
const INPUT = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid #E0E0E0',
  fontSize: 11,
  fontFamily: 'Montserrat',
  outline: 'none',
  boxSizing: 'border-box',
};
const REQUIRED = { color: '#C62828', marginLeft: 2 };
const ERR = { fontSize: 9, color: '#C62828', marginTop: 2, display: 'block' };
const HINT = { fontSize: 9, color: '#999', marginTop: 2, display: 'block' };
const SECTION = { fontSize: 10, fontWeight: 700, color: '#1B5E20', letterSpacing: 2, textTransform: 'uppercase', margin: '20px 0 12px' };

const blank = (me) => ({
  companyName: '',
  industry: '',
  vertical: '',
  phone: '',
  website: '',
  location: '',
  tier: '',
  leadSource: '',
  leadSourceDetail: '',
  notes: '',
  stage: DEFAULT_STAGE,
  designation: 'Lead',
  taskDescription: '',
  taskOwner: me,
  taskDue: '',
  taskTagged: [],
});

// The company fields a new account may start with (prefill).
const PREFILL_KEYS = ['companyName', 'industry', 'phone', 'website', 'location', 'notes'];
const prefillOf = (p) =>
  Object.fromEntries(PREFILL_KEYS.filter((k) => p && typeof p[k] === 'string' && p[k].trim()).map((k) => [k, p[k].trim()]));

// Blank stays blank. The form used to store '-' for empty Industry / Phone /
// Location, which then exported as junk and broke "is blank" filters (Paul #43).
const orNull = (v) => (v && v.trim() ? v.trim() : null);

export default function AccountForm({ onClose, initialData, prefill, onSaved, defaultDesignation = 'Lead' }) {
  const { updateAccount, fetchAll, users } = useApp();
  const { user } = useAuth();
  const me = (user && user.name) || '';
  const isEdit = !!initialData;

  const [f, setF] = useState(
    isEdit
      ? {
          ...blank(me),
          companyName: initialData.name || '',
          industry: initialData.industry && initialData.industry !== '-' ? initialData.industry : '',
          vertical: initialData.vertical || '',
          phone: initialData.phone && initialData.phone !== '-' ? initialData.phone : '',
          website: initialData.website || '',
          location: initialData.location && initialData.location !== '-' ? initialData.location : '',
          tier: initialData.tier || '',
          leadSource: initialData.leadSource || initialData.lead_source || '',
          leadSourceDetail: initialData.leadSourceDetail || initialData.lead_source_detail || '',
          notes: initialData.notes || '',
        }
      : { ...blank(me), ...(DESIGNATIONS.includes(defaultDesignation) ? { designation: defaultDesignation } : {}), ...prefillOf(prefill) }
  );
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(false);
  // an account made automatically for an opportunity is not counted as a lead
  // (auto_created, 10/12/26); ticking this saves it as one
  const untracked = isEdit && isUntrackedLead(initialData);
  const [trackAsLead, setTrackAsLead] = useState(false);

  const ownerOptions = Array.from(
    new Set([...(users || []).map((u) => u.name).filter(Boolean), ...(f.taskOwner ? [f.taskOwner] : [])])
  ).sort((a, b) => a.localeCompare(b));

  const set = (k) => (e) => {
    const v = e.target.value;
    setF((p) => ({ ...p, [k]: v }));
    setSaveError('');
    if (errors[k])
      setErrors((p) => {
        const n = { ...p };
        delete n[k];
        return n;
      });
  };

  const handleSave = useCallback(async () => {
    if (busy) return;
    const errs = {};
    if (!f.companyName.trim()) errs.companyName = 'Company name is required.';
    // an Account is not a lead: its lead source and first task are optional
    // (a first task half filled in is still checked)
    const isAccount = f.designation === 'Account';
    const taskStarted = !!(f.taskDescription.trim() || f.taskDue);
    if (!isEdit) {
      if (!f.stage) errs.stage = 'Pick the stage this lead is at.';
      if (!f.leadSource && !isAccount) errs.leadSource = 'Pick where this lead came from.';
      if (!isAccount || taskStarted) {
        const te = firstTaskErrors({ description: f.taskDescription, owner: f.taskOwner, due_date: f.taskDue });
        if (te.description) errs.taskDescription = te.description;
        if (te.owner) errs.taskOwner = te.owner;
        if (te.due_date) errs.taskDue = te.due_date;
      }
    }
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const initials = f.companyName
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase();
    const fields = {
      name: f.companyName.trim(),
      initials,
      industry: orNull(f.industry),
      vertical: orNull(f.vertical),
      phone: orNull(f.phone),
      location: orNull(f.location),
      website: orNull(f.website),
      tier: orNull(f.tier), // A / B / C - no default, it comes off the scoring model
      leadSource: orNull(f.leadSource),
      leadSourceDetail: orNull(f.leadSourceDetail),
      notes: orNull(f.notes),
      opportunityKey: f.companyName.trim(),
    };
    setBusy(true);
    setSaveError('');
    try {
      let row = null;
      if (isEdit) {
        // saving the account form is someone filling it in: an account made
        // from an opportunity's customer name alone is no longer flagged
        const r = await updateAccount(initialData.id, {
          ...fields,
          needsDetails: false,
          ...(untracked && trackAsLead ? { autoCreated: false } : {}),
        });
        if (r && r.ok === false) {
          setSaveError(r.error || 'Could not save - try again.');
          return;
        }
      } else {
        // POSTed here rather than through addAccount so a refusal (e.g. an
        // incomplete first task) is shown instead of silently dropped.
        // Owner defaults server-side to the signed-in user.
        const res = await fetch('/api/accounts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: fields.name,
            initials,
            industry: fields.industry,
            vertical: fields.vertical,
            phone: fields.phone,
            location: fields.location,
            website: fields.website,
            tier: fields.tier,
            lead_source: fields.leadSource,
            lead_source_detail: fields.leadSourceDetail,
            notes: fields.notes,
            opportunity_key: fields.opportunityKey,
            stage: f.stage,
            designation: f.designation || 'Lead',
            ...(!isAccount || taskStarted
              ? {
                  first_task: taskPayload({
                    description: f.taskDescription,
                    owner: f.taskOwner,
                    due_date: f.taskDue,
                    tagged: f.taskTagged,
                  }),
                }
              : {}),
          }),
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          setSaveError(b.error || 'Could not save the account - try again.');
          return;
        }
        row = await res.json().catch(() => null);
        await fetchAll();
      }
      if (onSaved) onSaved(row || (isEdit ? { ...initialData, ...fields } : null));
      setToast(true);
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      console.error('AccountForm save error:', err);
      setSaveError('Could not save the account - try again.');
    } finally {
      setBusy(false);
    }
  }, [f, isEdit, initialData, updateAccount, fetchAll, onClose, onSaved, busy, untracked, trackAsLead]);

  const errBorder = (k) => (errors[k] ? { border: '1px solid #C62828' } : {});
  const errText = (k) => (errors[k] ? <span style={ERR}>{errors[k]}</span> : null);
  const legacySource = f.leadSource && !LEAD_SOURCES.includes(f.leadSource);
  const legacyVertical = f.vertical && !VERTICALS.includes(f.vertical);

  return (
    <div>
      {toast && <Toast message={isEdit ? 'Account updated' : 'Account saved'} onDone={() => setToast(false)} />}

      <div style={{ padding: '24px 28px' }}>
        <div style={{ ...SECTION, marginTop: 0 }}>Account Information</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 24px' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={LABEL}>
              Company Name<span style={REQUIRED}>*</span>
            </label>
            <input
              style={{ ...INPUT, ...errBorder('companyName') }}
              value={f.companyName}
              onChange={set('companyName')}
              placeholder="e.g. Acme Manufacturing"
            />
            {errText('companyName')}
          </div>
          <div>
            <label style={LABEL}>Vertical</label>
            <select style={INPUT} value={f.vertical} onChange={set('vertical')}>
              <option value="">---</option>
              {VERTICALS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
              {legacyVertical && <option value={f.vertical}>{f.vertical} (old value)</option>}
            </select>
            <span style={HINT}>What the company primarily makes. Its sites start with it, and a site can set its own.</span>
          </div>
          <div>
            <label style={LABEL}>Account Tier</label>
            <select style={INPUT} value={f.tier} onChange={set('tier')}>
              <option value="">---</option>
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  Tier {t} · {TIER_HINT[t]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={LABEL}>Lead source{!isEdit && f.designation !== 'Account' && <span style={REQUIRED}>*</span>}</label>
            <select style={{ ...INPUT, ...errBorder('leadSource') }} value={f.leadSource} onChange={set('leadSource')}>
              {!f.leadSource && (
                <option value="" disabled>
                  Pick one...
                </option>
              )}
              {LEAD_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
              {legacySource && <option value={f.leadSource}>{f.leadSource} (old value)</option>}
            </select>
            {errText('leadSource')}
          </div>
          <div>
            <label style={LABEL}>Lead source detail</label>
            <input
              style={INPUT}
              value={f.leadSourceDetail}
              onChange={set('leadSourceDetail')}
              placeholder="Which ad, which show, who referred"
            />
          </div>
          {!isEdit && (
            <>
              <div>
                <label style={LABEL}>
                  Stage<span style={REQUIRED}>*</span>
                </label>
                <select style={{ ...INPUT, ...errBorder('stage') }} value={f.stage} onChange={set('stage')}>
                  {/* a brand-new record has no logged meeting, so it can't start at the meeting-gated stage (L-09) */}
                  {CRM_STAGES.filter((s) => s !== MEETING_GATE_STAGE).map((s) => (
                    <option key={s} value={s} title={STAGE_DEFINITIONS[s]}>
                      {s}
                    </option>
                  ))}
                </select>
                {errors.stage ? errText('stage') : <span style={HINT}>{STAGE_DEFINITIONS[f.stage]}</span>}
              </div>
              <div>
                <label style={LABEL}>Designation</label>
                <select style={INPUT} value={f.designation} onChange={set('designation')} aria-label="Designation">
                  {DESIGNATIONS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
                <span style={HINT}>
                  {f.designation === 'Customer'
                    ? "We already have a PO from them. A Customer can't be set back to Target, Lead or Account, and it stays off the Leads board."
                    : f.designation === 'Target'
                      ? DESIGNATION_DEFINITIONS.Target
                      : f.designation === 'Account'
                        ? `${DESIGNATION_DEFINITIONS.Account} A won opportunity makes it a Customer automatically.`
                        : 'Contact made, no PO yet. A won opportunity makes it a Customer automatically.'}
                </span>
              </div>
            </>
          )}
          <div>
            <label style={LABEL}>Industry (free text)</label>
            <input style={INPUT} value={f.industry} onChange={set('industry')} placeholder="e.g. Automotive Coatings" />
          </div>
          <div>
            <label style={LABEL}>Phone</label>
            <input style={INPUT} value={f.phone} onChange={set('phone')} placeholder="+1 (xxx) xxx-xxxx" />
          </div>
          <div>
            <label style={LABEL}>Website</label>
            <input style={INPUT} value={f.website} onChange={set('website')} placeholder="https://..." />
          </div>
          <div>
            <label style={LABEL}>Location / HQ</label>
            <input style={INPUT} value={f.location} onChange={set('location')} placeholder="e.g. Detroit, MI" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={LABEL}>Notes</label>
            <textarea style={{ ...INPUT, minHeight: 60, resize: 'vertical' }} value={f.notes} onChange={set('notes')} />
          </div>
        </div>

        {untracked && (
          <div style={{ marginTop: 16, padding: '10px 12px', background: '#FAFAFA', border: '1px solid #EEEEEE', borderRadius: 8 }}>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 700, color: '#333', cursor: 'pointer' }}
            >
              <input type="checkbox" checked={trackAsLead} onChange={(e) => setTrackAsLead(e.target.checked)} />
              Track as a lead
            </label>
            <span style={HINT}>
              Not tracked as a lead - made automatically for an opportunity, so it is not on the Leads board. Tick to put it there.
            </span>
          </div>
        )}

        {/* T-03: no lead starts without a next step */}
        {!isEdit && (
          <>
            <div style={SECTION}>
              First Task{f.designation === 'Account' ? ' (optional for an Account)' : <span style={REQUIRED}>*</span>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr', gap: '14px 16px' }}>
              <div>
                <label style={LABEL}>What happens next{f.designation !== 'Account' && <span style={REQUIRED}>*</span>}</label>
                <input
                  style={{ ...INPUT, ...errBorder('taskDescription') }}
                  value={f.taskDescription}
                  onChange={set('taskDescription')}
                  placeholder="e.g. Book a discovery call with the plant manager"
                />
                {errText('taskDescription')}
              </div>
              <div>
                <label style={LABEL}>
                  Owner<span style={REQUIRED}>*</span>
                </label>
                <select style={{ ...INPUT, ...errBorder('taskOwner') }} value={f.taskOwner} onChange={set('taskOwner')}>
                  <option value="">Pick one...</option>
                  {ownerOptions.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                {errText('taskOwner')}
              </div>
              <div>
                <label style={LABEL}>
                  Due date<span style={REQUIRED}>*</span>
                </label>
                <input type="date" style={{ ...INPUT, ...errBorder('taskDue') }} value={f.taskDue} onChange={set('taskDue')} />
                {errText('taskDue')}
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={LABEL} htmlFor="account-first-task-tagged">
                Tag people (optional)
              </label>
              <TagPeople
                id="account-first-task-tagged"
                value={f.taskTagged}
                people={ownerOptions}
                owner={f.taskOwner}
                onChange={(v) => setF((p) => ({ ...p, taskTagged: v }))}
              />
            </div>
            <span style={HINT}>Every lead needs an open task - even a "follow up" months out - so it never slips.</span>
          </>
        )}
      </div>

      {/* FOOTER */}
      <div
        style={{
          padding: '16px 24px',
          borderTop: '1px solid #F0F0F0',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0,
        }}
      >
        {saveError && <span style={{ flex: 1, fontSize: 10.5, fontWeight: 600, color: '#B71C1C' }}>{saveError}</span>}
        {!saveError && Object.keys(errors).length > 0 && (
          <span style={{ flex: 1, fontSize: 10.5, fontWeight: 600, color: '#B71C1C' }}>Fill in the fields marked in red.</span>
        )}
        <button
          onClick={onClose}
          style={{
            background: '#fff',
            color: '#555',
            border: '1px solid #E0E0E0',
            padding: '9px 20px',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 11,
            cursor: 'pointer',
            fontFamily: 'Montserrat',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={busy}
          style={{
            background: '#1B5E20',
            color: '#fff',
            border: 'none',
            padding: '9px 20px',
            borderRadius: 6,
            fontWeight: 700,
            fontSize: 11,
            cursor: busy ? 'wait' : 'pointer',
            fontFamily: 'Montserrat',
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? 'Saving...' : isEdit ? 'Update Account' : 'Save Account'}
        </button>
      </div>
    </div>
  );
}
